import * as React from 'react';

import {
  Alert,
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

import { DollarFormatter, Page, PercentageFormatter } from '@src/components';
import { fetchClientSummaryByDate, fetchVerticalSummary } from '@src/api';
import { formatDateToYMD } from '@src/utils/dateUtils';

const METRIC_CONFIGS = [
  {
    key: 'spend',
    label: 'Spend',
    direction: 'up',
    formatter: (value) =>
      value == null ? '—' : <DollarFormatter value={value} />,
  },
  {
    key: 'cpl',
    label: 'Cost / Lead',
    direction: 'down',
    formatter: (value) =>
      value == null ? '—' : <DollarFormatter value={value} />,
  },
  {
    key: 'selfBookRate',
    label: 'Self Book Rate',
    direction: 'up',
    formatter: (value) =>
      value == null ? '—' : <PercentageFormatter value={value} />, // ratio input
  },
  {
    key: 'acceptedRate',
    label: 'Accepted Rate',
    direction: 'up',
    formatter: (value) =>
      value == null ? '—' : <PercentageFormatter value={value} />, // ratio input
  },
  {
    key: 'outOfAreaRate',
    label: 'Out of Area Rate',
    direction: 'down',
    formatter: (value) =>
      value == null ? '—' : <PercentageFormatter value={value} />, // ratio input
  },
];

const ensureDate = (input) => {
  const toStartOfDay = (date) => {
    const cloned = new Date(date.getTime());
    cloned.setHours(0, 0, 0, 0);
    return cloned;
  };

  if (input instanceof Date) {
    return Number.isNaN(input.getTime()) ? null : toStartOfDay(input);
  }

  if (typeof input === 'number') {
    const date = new Date(input);
    return Number.isNaN(date.getTime()) ? null : toStartOfDay(date);
  }

  if (typeof input === 'string') {
    const trimmed = input.trim();
    if (!trimmed) return null;

    const match = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (match) {
      const [, y, m, d] = match;
      const date = new Date(Number(y), Number(m) - 1, Number(d));
      return Number.isNaN(date.getTime()) ? null : toStartOfDay(date);
    }

    const parsed = Date.parse(trimmed);
    if (!Number.isNaN(parsed)) {
      const date = new Date(parsed);
      return Number.isNaN(date.getTime()) ? null : toStartOfDay(date);
    }
  }

  return null;
};

const normalizeDateKey = (input) => {
  if (!input) return null;
  if (typeof input === 'string') return input.slice(0, 10);
  if (input instanceof Date) return formatDateToYMD(input);
  return null;
};

const safeNumber = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const safeRatio = (numerator, denominator) => {
  const num = safeNumber(numerator);
  const denom = safeNumber(denominator);
  if (num === null || denom === null || denom === 0) return null;
  return num / denom;
};

const normalizeRatioCandidate = (value) => {
  const numeric = safeNumber(value);
  if (numeric === null) return null;
  if (Math.abs(numeric) > 1) {
    return numeric / 100;
  }
  return numeric;
};

const resolveSelfBookRate = (row = {}) => {
  const direct =
    normalizeRatioCandidate(row.self_book_rate_avg) ??
    normalizeRatioCandidate(row.self_book_rate) ??
    normalizeRatioCandidate(row.selfBookRate);

  if (direct !== null) {
    return direct;
  }

  return safeRatio(
    row.self_books_sum ??
      row.self_book_count_sum ??
      row.self_book_count ??
      row.selfBookCount,
    row.total_leads_sum ?? row.totalLeads ?? row.total_leads,
  );
};

const determineDateKeys = (rawRows, explicitKeys) => {
  if (Array.isArray(explicitKeys) && explicitKeys.length > 0) {
    return explicitKeys;
  }
  if (!Array.isArray(rawRows) || rawRows.length === 0) {
    return [];
  }
  return Array.from(
    new Set(
      rawRows
        .map((row) => {
          const rawDate = row?.date ?? row?.day ?? row?.date_ymd ?? row?.Date ?? null;
          const parsedDate = ensureDate(rawDate);
          return parsedDate ? formatDateToYMD(parsedDate) : normalizeDateKey(rawDate);
        })
        .filter((value) => typeof value === 'string' && value.length > 0),
    ),
  ).sort();
};

const buildMetricsFromDays = (orderedDays = []) => {
  const metrics = METRIC_CONFIGS.reduce((acc, config) => {
    const dailySeries = orderedDays.map((day) => ({
      date: day.date,
      value: safeNumber(day[config.key]),
    }));
    const validSeries = dailySeries.filter((point) => typeof point.value === 'number');
    const todayEntry = validSeries.length ? validSeries[validSeries.length - 1] : null;
    const yesterdayEntry = validSeries.length > 1 ? validSeries[validSeries.length - 2] : null;
    const average =
      validSeries.length > 0
        ? validSeries.reduce((sum, point) => sum + point.value, 0) / validSeries.length
        : null;
    const todayVsYesterday =
      todayEntry && yesterdayEntry ? todayEntry.value - yesterdayEntry.value : null;
    const yesterdayVsAverage =
      yesterdayEntry && average !== null ? yesterdayEntry.value - average : null;
    const todayTrend =
      typeof todayVsYesterday === 'number' && Math.abs(todayVsYesterday) > Number.EPSILON
        ? todayVsYesterday > 0
          ? 'up'
          : 'down'
        : 'neutral';

    acc[config.key] = {
      today: todayEntry?.value ?? null,
      yesterday: yesterdayEntry?.value ?? null,
      average,
      todayVsYesterday,
      yesterdayVsAverage,
      todayTrend,
    };
    return acc;
  }, {});

  const totalSpend = orderedDays.reduce(
    (sum, day) => (typeof day.spend === 'number' ? sum + day.spend : sum),
    0,
  );

  return { metrics, totalSpend };
};

const getLastSevenDaysRange = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const start = new Date(today);
  start.setDate(start.getDate() - 6);
  return [start, today];
};

const buildDateKeys = (startDate, endDate) => {
  const start = ensureDate(startDate);
  const end = ensureDate(endDate);
  if (!start || !end) return [];

  const rangeStart = start.getTime() <= end.getTime() ? start : end;
  const rangeEnd = start.getTime() <= end.getTime() ? end : start;

  const keys = [];
  const cursor = new Date(rangeStart);
  while (cursor.getTime() <= rangeEnd.getTime()) {
    keys.push(formatDateToYMD(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return keys;
};

/**
 * Fetch hook with a built-in 401 retry:
 * - Call API
 * - If 401, wait briefly (to let your global auth refresh finish), then retry once
 */
const useClientSummaryData = (startDate, endDate) => {
  const [rawRows, setRawRows] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    let isMounted = true;

    const load = async () => {
      const normalizedStart = ensureDate(startDate);
      const normalizedEnd = ensureDate(endDate);

      if (!normalizedStart || !normalizedEnd) {
        if (isMounted) {
          setRawRows([]);
          setError(null);
          setIsLoading(false);
        }
        return;
      }

      const payload = {
        date_after: formatDateToYMD(normalizedStart),
        date_before: formatDateToYMD(normalizedEnd),
        ordering: 'client__name,date',
      };

      console.log('[PT] fetching:', payload);

      const attempt = async (retry = false) => {
        try {
          const response = await fetchClientSummaryByDate(payload);
          const data = Array.isArray(response?.data)
            ? response.data
            : Array.isArray(response?.data?.results)
              ? response.data.results
              : [];
          console.log('[PT] response rows:', Array.isArray(data) ? data.length : 'not array');
          console.table(Array.isArray(data) ? data.slice(0, 5) : []);
          if (isMounted) {
            setRawRows(data);
            setError(null);
          }
        } catch (err) {
          console.log('[PT] fetch error:', err);
          const status = err?.response?.status;
          if (status === 401 && !retry) {
            await new Promise((r) => setTimeout(r, 350));
            return attempt(true);
          }
          if (isMounted) {
            setError(err);
            setRawRows([]);
          }
        }
      };

      setError(null);
      try {
        if (isMounted) setIsLoading(true);
        await attempt(false);
      } catch (err) {
        if (isMounted) {
          setError(err);
          setRawRows([]);
        }
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    setIsLoading(true);
    setError(null);
    load();

    return () => {
      isMounted = false;
    };
  }, [startDate, endDate]);

  return { rawRows, isLoading, error };
};

const useVerticalSummaryData = (startDate, endDate) => {
  const [rawRows, setRawRows] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    let isMounted = true;

    const load = async () => {
      const normalizedStart = ensureDate(startDate);
      const normalizedEnd = ensureDate(endDate);

      if (!normalizedStart || !normalizedEnd) {
        if (isMounted) {
          setRawRows([]);
          setError(null);
          setIsLoading(false);
        }
        return;
      }

      const dateKeys = buildDateKeys(normalizedStart, normalizedEnd);
      if (dateKeys.length === 0) {
        if (isMounted) {
          setRawRows([]);
          setError(null);
          setIsLoading(false);
        }
        return;
      }

      if (isMounted) {
        setIsLoading(true);
        setError(null);
      }

      const aggregatedRows = [];

      try {
        await Promise.all(
          dateKeys.map(async (dateKey) => {
            const payload = {
              date: dateKey,
              ordering: 'vertical',
            };

            const attempt = async (retry = false) => {
              try {
                const response = await fetchVerticalSummary(payload);
                const data = Array.isArray(response?.data)
                  ? response.data
                  : Array.isArray(response?.data?.results)
                    ? response.data.results
                    : [];
                data.forEach((row) => {
                  aggregatedRows.push({ ...row, date: dateKey });
                });
              } catch (err) {
                const status = err?.response?.status;
                if (status === 401 && !retry) {
                  await new Promise((r) => setTimeout(r, 350));
                  return attempt(true);
                }
                throw err;
              }
            };

            await attempt(false);
          }),
        );

        if (isMounted) {
          setRawRows(aggregatedRows);
          setError(null);
        }
      } catch (err) {
        if (isMounted) {
          setRawRows([]);
          setError(err);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    load();

    return () => {
      isMounted = false;
    };
  }, [startDate, endDate]);

  return { rawRows, isLoading, error };
};

const normalizeClients = (rawRows, dateKeys) => {
  if (!Array.isArray(rawRows) || rawRows.length === 0) {
    return [];
  }

  const grouped = new Map();

  rawRows.forEach((row = {}) => {
    const clientId =
      row.client_id ??
      row.client__id ??
      row.client ??
      row.id ??
      row.uuid ??
      row.clientId ??
      null;
    const clientName =
      typeof row.client__name === 'string'
        ? row.client__name
        : typeof row.client_name === 'string'
          ? row.client_name
          : typeof row.name === 'string'
            ? row.name
            : 'Unknown Client';
    const rawDate = row.date ?? row.day ?? row.date_ymd ?? row.Date ?? null;
    const parsedDate = ensureDate(rawDate);
    const dateKey = parsedDate ? formatDateToYMD(parsedDate) : normalizeDateKey(rawDate);

    if (!clientId) {
      return;
    }

    if (!dateKey) {
      return;
    }

    const identifier = clientId;
    const spend = safeNumber(
      row.total_spend_sum ?? row.spend_sum ?? row.spend ?? row.totalSpend ?? row.total_spend,
    );
    const cpl = safeNumber(
      row.cost_per_lead_avg ??
      row.cost_per_lead ??
      row.cpl_avg ??
      row.cpl,
    );
    const selfBookRate = resolveSelfBookRate(row);
    const acceptedRateFromSums = (() => {
      const acceptedSum = safeNumber(
        row.accepted_sum ??
          row.accepted_count_sum ??
          row.accepted_count ??
          row.accepted,
      );
      const rejectedSum = safeNumber(
        row.rejected_sum ??
          row.rejected_count_sum ??
          row.rejected_count ??
          row.rejected,
      );
      const denominator =
        (acceptedSum ?? 0) + (rejectedSum ?? 0);
      if (acceptedSum === null || !Number.isFinite(denominator) || denominator === 0) {
        return null;
      }
      return acceptedSum / denominator;
    })();
    const acceptedRate =
      acceptedRateFromSums ??
      safeNumber(row.accepted_rate_avg ?? row.accepted_avg ?? row.accepted_rate);
    const outOfAreaRateFromSums = safeRatio(
      row.out_of_areas_count_sum ?? row.out_of_area_sum ?? row.outOfAreaCount,
      row.total_leads_sum ?? row.totalLeads ?? row.total_leads,
    );
    const outOfAreaRate =
      outOfAreaRateFromSums ??
      safeNumber(
        row.out_of_area_avg ??
          row.out_of_area_rate_avg ??
          row.out_of_area_rate ??
          row.outOfAreaRate,
      );

    const bucket = grouped.get(identifier) ?? {
      clientId: identifier,
      clientName,
      byDate: new Map(),
    };

    bucket.byDate.set(dateKey, {
      date: dateKey,
      spend,
      cpl,
      selfBookRate,
      acceptedRate,
      outOfAreaRate,
    });

    grouped.set(identifier, bucket);
  });

  const computedDateKeys = determineDateKeys(rawRows, dateKeys);

  if (computedDateKeys.length === 0) {
    return [];
  }

  return Array.from(grouped.values()).map((bucket) => {
    const orderedDays = computedDateKeys.map((dateKey) => {
      const entry = bucket.byDate.get(dateKey);
      if (entry) return entry;
      return {
        date: dateKey,
        spend: null,
        cpl: null,
        selfBookRate: null,
        acceptedRate: null,
        outOfAreaRate: null,
      };
    });

    const { metrics, totalSpend } = buildMetricsFromDays(orderedDays);

    return {
      clientId: bucket.clientId,
      clientName: bucket.clientName,
      days: orderedDays,
      metrics,
      totalSpend,
    };
  });
};

const normalizeVerticals = (rawRows, dateKeys) => {
  if (!Array.isArray(rawRows) || rawRows.length === 0) {
    return [];
  }

  const grouped = new Map();

  rawRows.forEach((row = {}) => {
    const rawDate = row.date ?? row.day ?? row.date_ymd ?? row.Date ?? null;
    const parsedDate = ensureDate(rawDate);
    const dateKey = parsedDate ? formatDateToYMD(parsedDate) : normalizeDateKey(rawDate);
    if (!dateKey) {
      return;
    }

    const verticalLabel = typeof row.vertical === 'string' ? row.vertical.trim() : '';
    const verticalName = verticalLabel || 'Unspecified Vertical';
    const identifier = verticalName.toLowerCase() || 'unspecified-vertical';

    const spend = safeNumber(
      row.total_spend_sum ?? row.spend_sum ?? row.spend ?? row.totalSpend ?? row.total_spend,
    );
    const cpl = safeNumber(
      row.cost_per_lead_avg ??
      row.cost_per_lead ??
      row.cpl_avg ??
      row.cpl,
    );
    const selfBookRate = resolveSelfBookRate(row);
    const acceptedRateFromSums = (() => {
      const acceptedSum = safeNumber(
        row.accepted_sum ??
          row.accepted_count_sum ??
          row.accepted_count ??
          row.accepted,
      );
      const rejectedSum = safeNumber(
        row.rejected_sum ??
          row.rejected_count_sum ??
          row.rejected_count ??
          row.rejected,
      );
      const denominator =
        (acceptedSum ?? 0) + (rejectedSum ?? 0);
      if (acceptedSum === null || !Number.isFinite(denominator) || denominator === 0) {
        return null;
      }
      return acceptedSum / denominator;
    })();
    const acceptedRate =
      acceptedRateFromSums ??
      safeNumber(row.accepted_rate_avg ?? row.accepted_avg ?? row.accepted_rate);
    const outOfAreaRateFromSums = safeRatio(
      row.out_of_areas_count_sum ?? row.out_of_area_sum ?? row.outOfAreaCount,
      row.total_leads_sum ?? row.totalLeads ?? row.total_leads,
    );
    const outOfAreaRate =
      outOfAreaRateFromSums ??
      safeNumber(
        row.out_of_area_avg ??
          row.out_of_area_rate_avg ??
          row.out_of_area_rate ??
          row.outOfAreaRate,
      );

    const bucket = grouped.get(identifier) ?? {
      verticalId: identifier,
      verticalName,
      byDate: new Map(),
    };

    bucket.byDate.set(dateKey, {
      date: dateKey,
      spend,
      cpl,
      selfBookRate,
      acceptedRate,
      outOfAreaRate,
    });

    grouped.set(identifier, bucket);
  });

  const computedDateKeys = determineDateKeys(rawRows, dateKeys);

  if (computedDateKeys.length === 0) {
    return [];
  }

  return Array.from(grouped.values()).map((bucket) => {
    const orderedDays = computedDateKeys.map((dateKey) => {
      const entry = bucket.byDate.get(dateKey);
      if (entry) return entry;
      return {
        date: dateKey,
        spend: null,
        cpl: null,
        selfBookRate: null,
        acceptedRate: null,
        outOfAreaRate: null,
      };
    });

    const { metrics, totalSpend } = buildMetricsFromDays(orderedDays);

    return {
      verticalId: bucket.verticalId,
      verticalName: bucket.verticalName,
      days: orderedDays,
      metrics,
      totalSpend,
    };
  });
};

const buildAllClientsAggregate = (rawRows, dateKeys) => {
  if (!Array.isArray(rawRows) || rawRows.length === 0) {
    return null;
  }

  const totalsByDate = new Map();

  rawRows.forEach((row = {}) => {
    const rawDate = row?.date ?? row?.day ?? row?.date_ymd ?? row?.Date ?? null;
    const parsedDate = ensureDate(rawDate);
    const dateKey = parsedDate ? formatDateToYMD(parsedDate) : normalizeDateKey(rawDate);
    if (!dateKey) return;

    const bucket = totalsByDate.get(dateKey) ?? {
      date: dateKey,
      spend: 0,
      totalLeads: 0,
      accepted: 0,
      rejected: 0,
      outOfArea: 0,
      selfBooks: 0,
    };

    const spend = safeNumber(
      row.total_spend_sum ?? row.spend_sum ?? row.spend ?? row.totalSpend ?? row.total_spend,
    );
    const totalLeads = safeNumber(row.total_leads_sum ?? row.totalLeads ?? row.total_leads);
    const accepted = safeNumber(
      row.accepted_sum ?? row.accepted_count_sum ?? row.accepted_count ?? row.accepted,
    );
    const rejected = safeNumber(
      row.rejected_sum ?? row.rejected_count_sum ?? row.rejected_count ?? row.rejected,
    );
    const selfBooks = safeNumber(
      row.self_books_sum ??
        row.self_book_count_sum ??
        row.self_book_count ??
        row.selfBookCount,
    );
    const outOfArea = safeNumber(
      row.out_of_areas_count_sum ?? row.out_of_area_sum ?? row.outOfAreaCount,
    );

    if (typeof spend === 'number') bucket.spend += spend;
    if (typeof totalLeads === 'number') bucket.totalLeads += totalLeads;
    if (typeof accepted === 'number') bucket.accepted += accepted;
    if (typeof rejected === 'number') bucket.rejected += rejected;
    if (typeof outOfArea === 'number') bucket.outOfArea += outOfArea;
    if (typeof selfBooks === 'number') bucket.selfBooks += selfBooks;

    totalsByDate.set(dateKey, bucket);
  });

  if (totalsByDate.size === 0) {
    return null;
  }

  const computedDateKeys = determineDateKeys(rawRows, dateKeys);
  if (computedDateKeys.length === 0) {
    return null;
  }

  const orderedDays = computedDateKeys.map((dateKey) => {
    const entry = totalsByDate.get(dateKey);
    if (!entry) {
      return {
        date: dateKey,
        spend: null,
        cpl: null,
        selfBookRate: null,
        acceptedRate: null,
        outOfAreaRate: null,
      };
    }

    const acceptedRate = (() => {
      const denominator = (entry.accepted ?? 0) + (entry.rejected ?? 0);
      if (typeof entry.accepted !== 'number' || denominator === 0) {
        return null;
      }
      return entry.accepted / denominator;
    })();

    return {
      date: dateKey,
      spend: typeof entry.spend === 'number' ? entry.spend : null,
      cpl: safeRatio(entry.spend, entry.totalLeads),
      selfBookRate: safeRatio(entry.selfBooks, entry.totalLeads),
      acceptedRate,
      outOfAreaRate: safeRatio(entry.outOfArea, entry.totalLeads),
    };
  });

  const { metrics, totalSpend } = buildMetricsFromDays(orderedDays);

  return {
    clientId: 'ALL_CLIENTS',
    clientName: 'All Clients',
    days: orderedDays,
    metrics,
    totalSpend,
  };
};

const buildAlphaColor = (theme, paletteKey, opacity) => {
  const channel = theme.vars?.palette?.[paletteKey]?.mainChannel;
  const color = theme.palette?.[paletteKey]?.main;
  if (channel) {
    return `rgba(${channel} / ${opacity})`;
  }
  return alpha(color, opacity);
};

const getTrendStyles = (trend, configDirection, theme) => {
  if (trend === 'neutral') return {};
  const isPositive = trend === 'up';
  const positiveIsGood = configDirection === 'up';
  const isGood = isPositive === positiveIsGood;
  const paletteKey = isGood ? 'success' : 'error';
  const baseColor = theme.palette?.[paletteKey]?.main;
  return {
    color: baseColor,
    backgroundColor: buildAlphaColor(theme, paletteKey, 0.12),
    paletteKey,
  };
};

const isCurrencyMetric = (key) => key === 'spend' || key === 'cpl';

const formatDeltaLabel = (key, delta) => {
  if (typeof delta !== 'number') return '—';
  const sign = delta > 0 ? '+' : delta < 0 ? '-' : '';
  const magnitude = Math.abs(delta);
  if (isCurrencyMetric(key)) {
    return `${sign}$${magnitude.toFixed(2)}`;
  }
  return `${sign}${(magnitude * 100).toFixed(1)}%`;
};

const getTrendArrow = (trend) => {
  if (trend === 'up') return '▲';
  if (trend === 'down') return '▼';
  return '—';
};

const SUBTLE_CHANGE_THRESHOLD = 0.1; // 10%
const NEAR_ZERO_THRESHOLD = 1e-6;

const isSubtleChange = (delta, average) => {
  if (typeof delta !== 'number' || Math.abs(delta) < Number.EPSILON) {
    return false;
  }
  if (typeof average !== 'number' || Math.abs(average) < NEAR_ZERO_THRESHOLD) {
    return false;
  }
  const relativeDelta = Math.abs(delta) / Math.abs(average);
  return relativeDelta <= SUBTLE_CHANGE_THRESHOLD;
};

const getChipColor = (trend, configDirection, delta, average) => {
  if (trend === 'neutral') return 'default';
  if (isSubtleChange(delta, average)) return 'warning';
  const isPositive = trend === 'up';
  const positiveIsGood = configDirection === 'up';
  return isPositive === positiveIsGood ? 'success' : 'error';
};

const getTrendFromDelta = (delta) => {
  if (typeof delta !== 'number' || Math.abs(delta) < Number.EPSILON) {
    return 'neutral';
  }
  return delta > 0 ? 'up' : 'down';
};

const PerformanceTrends = () => {
  const theme = useTheme();

  // Default to last 7 days
  const defaultRangeRef = React.useRef(getLastSevenDaysRange());
  const [startDate] = React.useState(defaultRangeRef.current[0]);
  const [endDate] = React.useState(defaultRangeRef.current[1]);

  const normalizedStartDate = React.useMemo(
    () => ensureDate(startDate) ?? defaultRangeRef.current[0],
    [startDate],
  );
  const normalizedEndDate = React.useMemo(
    () => ensureDate(endDate) ?? defaultRangeRef.current[1],
    [endDate],
  );

  const dateKeys = React.useMemo(
    () => buildDateKeys(normalizedStartDate, normalizedEndDate),
    [normalizedStartDate, normalizedEndDate],
  );

  const {
    rawRows: clientRows,
    isLoading: isClientLoading,
    error: clientError,
  } = useClientSummaryData(
    normalizedStartDate,
    normalizedEndDate,
  );
  const {
    rawRows: verticalRows,
    isLoading: isVerticalLoading,
    error: verticalError,
  } = useVerticalSummaryData(
    normalizedStartDate,
    normalizedEndDate,
  );

  const error = clientError || verticalError;

  const clients = React.useMemo(
    () => normalizeClients(clientRows, dateKeys),
    [clientRows, dateKeys],
  );
  const allClientsSummary = React.useMemo(
    () => buildAllClientsAggregate(clientRows, dateKeys),
    [clientRows, dateKeys],
  );
  const verticals = React.useMemo(
    () => normalizeVerticals(verticalRows, dateKeys),
    [verticalRows, dateKeys],
  );

  const sortedClients = React.useMemo(
    () => [...clients].sort((a, b) => (b.totalSpend || 0) - (a.totalSpend || 0)),
    [clients],
  );

  const visibleClients = React.useMemo(
    () => sortedClients.filter((client) => (client.totalSpend ?? 0) > 0),
    [sortedClients],
  );
  const visibleVerticals = React.useMemo(
    () => verticals.filter((vertical) => (vertical.totalSpend ?? 0) > 0),
    [verticals],
  );

  const renderMetricCell = (client, config) => {
    const metric = client.metrics[config.key] ?? {};
    const yesterdayTrend = getTrendFromDelta(metric.yesterdayVsAverage);
    const chipColor = getChipColor(
      yesterdayTrend,
      config.direction,
      metric.yesterdayVsAverage,
      metric.average,
    );
    const deltaArrow = getTrendArrow(yesterdayTrend);
    const deltaLabel = formatDeltaLabel(config.key, metric.yesterdayVsAverage);

    return (
      <TableCell
        key={config.key}
        align="center"
        sx={{ minWidth: 200, verticalAlign: 'middle', px: 1, py: 0.75 }}
      >
        <Stack spacing={0.75} alignItems="stretch">
          <Box
            display="grid"
            gridTemplateColumns="repeat(2, minmax(0, 1fr))"
            columnGap={1}
            rowGap={0.25}
            width="100%"
          >
            <Typography variant="caption" color="text.secondary" align="center">
              Yesterday
            </Typography>
            <Typography variant="caption" color="text.secondary" align="center">
              Today
            </Typography>
            <Typography variant="body2" fontWeight={600} align="center">
              {config.formatter(metric.yesterday ?? null)}
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center">
              {config.formatter(metric.today ?? null)}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
            <Typography variant="caption" color="text.secondary">
              Avg {config.formatter(metric.average ?? null)}
            </Typography>
            <Chip
              size="small"
              color={chipColor}
              label={`${deltaArrow} ${deltaLabel} vs 7d avg`}
              variant={chipColor === 'default' ? 'outlined' : 'filled'}
              sx={{ fontSize: 11, height: 22, px: 0.75 }}
            />
          </Stack>
        </Stack>
      </TableCell>
    );
  };

  return (
    <Page title="Performance Trends" fullWidth>
      <Stack spacing={3}>
        {error && (
          <Alert severity="error">
            {String(error?.response?.status) === '401'
              ? 'Session not ready yet. Please refresh or try again.'
              : 'Unable to load the client summary data. Please try again.'}
          </Alert>
        )}

        {allClientsSummary && (
          <Card sx={{ width: '100%' }}>
            <CardHeader
              title="All Clients Summary"
              subheader="Aggregate metrics across every client for the selected period."
            />
            <Divider />
            <CardContent sx={{ p: 0 }}>
              <TableContainer component={Paper}>
                <Table size="small" sx={{ minWidth: 1600 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          position: 'sticky',
                          left: 0,
                          zIndex: 2,
                          backgroundColor: 'background.paper',
                          width: 240,
                        }}
                      >
                        All Clients
                      </TableCell>
                      {METRIC_CONFIGS.map((config) => (
                        <TableCell key={config.key} align="center" sx={{ minWidth: 200 }}>
                          <Typography variant="subtitle2">{config.label}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Last 7 days
                          </Typography>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow hover sx={{ height: 72 }}>
                      <TableCell
                        sx={{
                          position: 'sticky',
                          left: 0,
                          backgroundColor: 'background.paper',
                          zIndex: 1,
                          width: 240,
                        }}
                      >
                        <Typography variant="subtitle2">{allClientsSummary.clientName}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Combined performance
                        </Typography>
                      </TableCell>
                      {METRIC_CONFIGS.map((config) => renderMetricCell(allClientsSummary, config))}
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        )}

        <Card sx={{ width: '100%' }}>
          <CardHeader
            title="Vertical Performance Pulse"
            subheader="Yesterday vs 7-day averages across each vertical."
          />
          <Divider />
          <CardContent sx={{ p: 0 }}>
            {isVerticalLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 8 }}>
                <Stack spacing={2} alignItems="center">
                  <CircularProgress size={28} />
                  <Typography variant="body2" color="text.secondary">
                    Loading vertical summary metrics…
                  </Typography>
                </Stack>
              </Box>
            ) : (
              <TableContainer
                component={Paper}
                sx={{ maxHeight: '75vh', minHeight: 320, width: '100%' }}
              >
                <Table stickyHeader size="small" sx={{ width: '100%', minWidth: 1600 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          position: 'sticky',
                          left: 0,
                          zIndex: 2,
                          backgroundColor: 'background.paper',
                          width: 240,
                        }}
                      >
                        Vertical
                      </TableCell>
                      {METRIC_CONFIGS.map((config) => (
                        <TableCell key={config.key} align="center" sx={{ minWidth: 200 }}>
                          <Typography variant="subtitle2">{config.label}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Last 7 days
                          </Typography>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visibleVerticals.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={METRIC_CONFIGS.length + 1} align="center" sx={{ py: 6 }}>
                          <Typography variant="body2" color="text.secondary">
                            No vertical summary data recorded in the selected period.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      visibleVerticals.map((vertical) => (
                        <TableRow key={vertical.verticalId} hover sx={{ height: 72 }}>
                          <TableCell
                            sx={{
                              position: 'sticky',
                              left: 0,
                              backgroundColor: 'background.paper',
                              zIndex: 1,
                              width: 240,
                            }}
                          >
                            <Typography variant="subtitle2">{vertical.verticalName}</Typography>
                          </TableCell>
                          {METRIC_CONFIGS.map((config) =>
                            renderMetricCell(vertical, config),
                          )}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        <Card sx={{ width: '100%' }}>
          <CardHeader
            title="Client Performance Pulse"
            subheader="Yesterday vs 7-day averages for Spend, CPL, Self Book Rate, Accepted Rate, and Out of Area Rate across every client."
          />
          <Divider />
          <CardContent sx={{ p: 0 }}>
            {isClientLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 8 }}>
                <Stack spacing={2} alignItems="center">
                  <CircularProgress size={28} />
                  <Typography variant="body2" color="text.secondary">
                    Loading the latest client summary metrics…
                  </Typography>
                </Stack>
              </Box>
            ) : (
              <TableContainer
                component={Paper}
                sx={{ maxHeight: '75vh', minHeight: 520, width: '100%' }}
              >
                <Table stickyHeader size="small" sx={{ width: '100%', minWidth: 1600 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          position: 'sticky',
                          left: 0,
                          zIndex: 2,
                          backgroundColor: 'background.paper',
                          width: 240,
                        }}
                      >
                        Client
                      </TableCell>
                      {METRIC_CONFIGS.map((config) => (
                        <TableCell key={config.key} align="center" sx={{ minWidth: 200 }}>
                          <Typography variant="subtitle2">{config.label}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Last 7 days
                          </Typography>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visibleClients.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={METRIC_CONFIGS.length + 1} align="center" sx={{ py: 6 }}>
                          <Typography variant="body2" color="text.secondary">
                            No client summary data with spend recorded in the past week.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      visibleClients.map((client) => (
                        <TableRow key={client.clientId} hover sx={{ height: 72 }}>
                          <TableCell
                            sx={{
                              position: 'sticky',
                              left: 0,
                              backgroundColor: 'background.paper',
                              zIndex: 1,
                              width: 240,
                            }}
                          >
                            <Typography variant="subtitle2">{client.clientName}</Typography>
                    
                          </TableCell>
                          {METRIC_CONFIGS.map((config) =>
                            renderMetricCell(client, config),
                          )}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </Stack>
    </Page>
  );
};

export default PerformanceTrends;
